import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Input, Button } from '@components';
import { UploadDocumentData } from '../types';

interface UploadDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UploadDocumentData) => void;
  knowledgeBaseId: string;
}

export const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  knowledgeBaseId,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: '',
    tags: '',
  });
  const [file, setFile] = useState<File | null>(null);
  const [tagList, setTagList] = useState<string[]>([]);
  const [currentTag, setCurrentTag] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (file) {
      onSubmit({
        title: formData.title,
        knowledgeBase: knowledgeBaseId,
        file,
        tags: tagList.join(','),
      });
      setFormData({ title: '', tags: '' });
      setFile(null);
      setTagList([]);
      setCurrentTag('');
      onClose();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // No title from the file name: it is part of every chunk's embedding, and file
    // names rarely name the content
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleTagKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (currentTag.trim() && !tagList.includes(currentTag.trim()) && tagList.length < 5) {
        setTagList(prev => [...prev, currentTag.trim()]);
        setCurrentTag('');
      }
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTagList(prev => prev.filter(tag => tag !== tagToRemove));
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({ title: '', tags: '' });
      setFile(null);
      setTagList([]);
      setCurrentTag('');
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('knowledgeBase.uploadDocument')}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="p-1 h-8 w-8">
            <span className="material-icons">close</span>
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="document-title"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('knowledgeBase.documentTitleRequired')}
            </label>
            <Input
              id="document-title"
              type="text"
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              placeholder={t('knowledgeBase.enterDocumentTitle')}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('knowledgeBase.descriptiveNameForDocument')}
            </p>
          </div>

          <div>
            <label
              htmlFor="document-file"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('knowledgeBase.fileRequired')}
            </label>
            <input
              id="document-file"
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.txt,.md"
              required
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/20 dark:file:text-blue-400"
            />
            <p className="text-xs text-gray-500 mt-1">{t('knowledgeBase.supportedFormats')}</p>
          </div>

          <div>
            <label
              htmlFor="document-tags"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('knowledgeBase.tags')}{' '}
              {tagList.length > 0 && (
                <span className="text-xs text-gray-500 font-normal">({tagList.length}/5)</span>
              )}
            </label>
            <div className="space-y-2">
              <Input
                id="document-tags"
                type="text"
                value={currentTag}
                onChange={e => setCurrentTag(e.target.value)}
                onKeyPress={handleTagKeyPress}
                placeholder={
                  tagList.length >= 5
                    ? t('knowledgeBase.maximum5TagsReached')
                    : t('knowledgeBase.enterTagsAndPressEnter')
                }
                disabled={tagList.length >= 5}
                className={tagList.length >= 5 ? 'opacity-50 cursor-not-allowed' : ''}
              />
              {tagList.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tagList.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 text-xs rounded-full"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                      >
                        <span className="material-icons text-sm">close</span>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t('knowledgeBase.optionalTagsDescription')}
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('knowledgeBase.cancel')}
            </Button>
            <Button
              type="submit"
              variant="gradient-multi"
              className="flex items-center gap-2"
              disabled={!file}
            >
              <span className="material-icons">upload_file</span>
              {t('knowledgeBase.uploadDocument')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
